import React, { useEffect, useState, useRef } from "react";
import { Input, Button, List, Avatar, Typography, Space, Card, Flex, Upload, UploadProps, Spin, message } from "antd";
import { observer } from "mobx-react-lite";
import { useStore } from "../../stores/root-store-context";
import { MessageOutlined, PaperClipOutlined, SendOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthorType } from "../../api/TicketApi";


dayjs.extend(relativeTime);

const { Text } = Typography;;
export const TicketChat = observer(({ ticketId, mode }: { ticketId: string, mode: 'customer' | 'support' }) => {
  const { t } = useTranslation();

  // const [text, setText] = useState("");
  // const [fileList, setFileList] = useState<File[]>([]);
  const { ticketChatStore, applicationStore } = useStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ticketChatStore.connect(ticketId);
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticketChatStore.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async () => {
    if (ticketChatStore.text.trim()) {
      console.log(ticketChatStore.fileList);
      let authorType = (applicationStore.company.SupportSystem) && mode === 'support' ? AuthorType.Support : AuthorType.User;
      let roles = (applicationStore.company.SupportSystem) && mode === 'support' ? "SupportManager" : applicationStore.user.roles?.map(role => role.name)?.join(',');

      await ticketChatStore.addMessage(ticketId, ticketChatStore.text,
        applicationStore.user.firstName + ' ' + applicationStore.user.lastName, applicationStore.user.email, roles, authorType, ticketChatStore.fileList).catch((error) => {
          console.error("Error adding message:", error);
        });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleBeforeUpload: UploadProps['beforeUpload'] = (file, files) => {
    const isLt30M = file.size / 1024 / 1024 <= 10;

    if (!isLt30M) {
      message.error(`${file.name}: ${t("FileSizeLimit", { size: 10 })}`);
      return Upload.LIST_IGNORE;
    }
  
    if (ticketChatStore.fileList.length >= 3) {
      message.warning(t("MaxFilesLimit", { count: 3 }));
      return Upload.LIST_IGNORE;
    }
  
    ticketChatStore.setFileList([...ticketChatStore.fileList, file]);
    return false;
  };

  const handleRemove = (fileToRemove: any) => {
    ticketChatStore.setFileList(ticketChatStore.fileList.filter((file: any) => file.uid !== fileToRemove.uid));
  };

  const props: UploadProps = {
    beforeUpload: (file, fileList) => {
      const formData = new FormData();
      fileList.forEach(file => {
        formData.append('files', file);
      });


      return false;
    },
    multiple: true,
    showUploadList: true,
  };

  return (
    <Card
      title={
        <Space>
          <MessageOutlined />
          <Text strong>{t("TicketChat")}</Text>
        </Space>
      }
      style={{ width: "100%", display: "flex", flexDirection: "column" }}
      bodyStyle={{ padding: 0 }}
    >
      <div
        style={{
          height: "400px",
          overflowY: "auto",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <List
          dataSource={ticketChatStore.messages}
          renderItem={(msg) => {
            const isCurrentUser = (msg.email == applicationStore.user.email && msg.authorType == AuthorType.User) || msg.authorType == AuthorType.User;

            return (
              <List.Item
                style={{
                  padding: "8px 0",
                  alignSelf: isCurrentUser ? "flex-start" : "flex-end",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isCurrentUser ? "flex-start" : "flex-end",
                  }}
                >
                  <Space align="center" size="small">
                    {!isCurrentUser && (
                      <Avatar size="small" style={{ backgroundColor: "#1890ff" }}>
                        {msg.authorType == AuthorType.Support ? "SM" : msg.email?.charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                    <Flex vertical justify="center" align="center">
                      <Text strong>{msg.authorType == AuthorType.Support ? "Support Manager" : msg.author}</Text>
                      <Text strong>{msg.authorType == AuthorType.Support ? "" : msg.email}</Text>
                      <Text strong>{msg.authorType == AuthorType.Support ? "" : msg.role}</Text>
                    </Flex>
                    {isCurrentUser && (
                      <Avatar size="small" style={{ backgroundColor: "#52c41a" }}>
                        {msg.email?.charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                  </Space>

                  <Card
                    bordered
                    size="small"
                    style={{
                      marginTop: 4,
                      backgroundColor: isCurrentUser ? "#e6f7ff" : "#f5f5f5",
                      textAlign: isCurrentUser ? "right" : "left",
                      maxWidth: 400,
                      wordBreak: "break-word",
                    }}
                  >
                    <div>{msg.messageText}</div>

                    {/* Вивід прикріплень */}
                    {msg.attachments && msg.attachments?.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        {msg.attachments.map((file) => {
                          const isImage = /\.(png|jpe?g|gif|bmp|webp)$/i.test(file.fileName);
                          return (
                            <div key={file.id} style={{ marginTop: 4 }}>
                              {isImage ? (
                                <img
                                  src={file.secureUrl}
                                  alt={file.displayName}
                                  style={{
                                    maxWidth: "100%",
                                    borderRadius: 4,
                                    border: "1px solid #ddd",
                                  }}
                                />
                              ) : (
                                <Link to={file.secureUrl} target="_blank" download>
                                  <PaperClipOutlined /> {file.displayName}
                                </Link>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>

                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                    {dayjs(msg.createdAt).fromNow()}
                  </Text>
                </div>
              </List.Item>
            );
          }}
        />
        <div ref={messagesEndRef} />
      </div>
      <Spin spinning={ticketChatStore.isLoading}>

        <div style={{ padding: "16px", borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <Input.TextArea
            value={ticketChatStore.text}
            onChange={(e) => ticketChatStore.setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Напишіть повідомлення..."
            rows={3}
            autoSize={{ minRows: 3, maxRows: 5 }}
            style={{ marginBottom: 8 }}
          />
          <Flex justify="space-between">
            <Upload
              beforeUpload={handleBeforeUpload}
              onRemove={handleRemove}
              fileList={ticketChatStore.fileList as any}
              multiple
            >
              <Button
                type="primary"
                style={{ alignSelf: "flex-end" }}
                icon={<UploadOutlined />}
              >
                {t('AddFiles')}
              </Button>
            </Upload>
            <div style={{ marginTop: 8, color: "#999" }}>
              {t("UploadLimitHint")}
            </div>
            <Button
              type="primary"
              style={{ alignSelf: "flex-end" }}
              onClick={sendMessage}
              disabled={!ticketChatStore.text.trim()}
              icon={<SendOutlined />}
            >
              {t('Send')}
            </Button>
          </Flex>
        </div>
      </Spin>

    </Card>
  );
});